/**
 * User Management Query Hooks
 *
 * React Query hooks for managing user data including CRUD operations,
 * role management, status toggling, and password resets.
 *
 * @module userQueries
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMockRoles } from './roleQueries';
import { createPersistedMockData } from '../mockDataManager';
import { gobodoUserProvider } from '../providers/gobodoUserProvider';
import type { Role } from '../providers/roleProvider';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';

// ============================================
// TYPES & INTERFACES
// ============================================

export type UserStatus = 'all' | 'active' | 'inactive';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phone?: string;
  title?: string;
  department?: string;
  location?: string;
  // Multiple roles support
  roles?: Role[];
  roleIds?: string[];
  roleNames?: string[];
  // Legacy single role fields (deprecated, kept for backward compatibility)
  roleId?: string;
  roleName?: string;
  profilePicture?: string;
  isActive: boolean;
  lastLogin?: string;
  createdDate: string;
  createdBy: string;
  lastModified: string;
  locale?: string;
  timezone?: string;
  emailSignature?: string;
  keycloakUserId?: string; // Keycloak token.sub
  /** Gobodo graph object key when present (UsersApi / graph); prefer for Case OwnerRelField when set. */
  graphObjectKey?: string;
}

export interface CreateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phone?: string;
  title?: string;
  department?: string;
  roleIds?: string[]; // Array of role IDs to assign to the user
  timezone?: string;
  locale?: string;
  isActive?: boolean;
  keycloakUserId?: string; // Keycloak token.sub for linking Keycloak user to Gobodo user
  /** Initial password for the new account. When not provided, REACT_APP_DEFAULT_USER_PASSWORD is used (Gobodo only). */
  password?: string;
}

export interface UpdateUserPayload {
  userId: string;
  updates: Partial<User>;
}

export interface UserFilters {
  searchQuery?: string;
  status?: UserStatus;
  roleId?: string;
  department?: string;
  includeRoles?: boolean;
}

// ============================================
// MOCK DATA
// ============================================

const defaultMockUsers: User[] = [
  {
    id: 'user-1',
    firstName: 'Christina',
    lastName: 'van Tillo',
    email: 'christina@regalproducthub.com',
    username: 'cvantillo',
    phone: '+1 (555) 123-4567',
    title: 'System Administrator',
    department: 'IT',
    roleId: 'role-admin',
    roleName: 'System Administrator',
    profilePicture: 'https://i.pravatar.cc/150?img=1',
    isActive: true,
    lastLogin: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
    createdDate: new Date('2024-01-15').toISOString(),
    createdBy: 'System',
    lastModified: new Date('2024-03-10').toISOString(),
    locale: 'en_US',
    timezone: 'America/New_York'
  },
  {
    id: 'user-2',
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@regalproducthub.com',
    username: 'jsmith',
    phone: '+1 (555) 234-5678',
    title: 'Credit Analyst',
    department: 'Credit Operations',
    roleId: 'role-analyst',
    roleName: 'Credit Analyst',
    profilePicture: 'https://i.pravatar.cc/150?img=12',
    isActive: true,
    lastLogin: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    createdDate: new Date('2024-02-01').toISOString(),
    createdBy: 'user-1',
    lastModified: new Date('2024-03-05').toISOString(),
    locale: 'en_US',
    timezone: 'America/Chicago'
  },
  {
    id: 'user-3',
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah.johnson@regalproducthub.com',
    username: 'sjohnson',
    phone: '+1 (555) 345-6789',
    title: 'Customer Service Representative',
    department: 'Customer Support',
    roleId: 'role-support',
    roleName: 'Customer Support',
    isActive: true,
    lastLogin: new Date(Date.now() - 1000 * 60 * 15).toISOString(), // 15 minutes ago
    createdDate: new Date('2024-02-10').toISOString(),
    createdBy: 'user-1',
    lastModified: new Date('2024-02-20').toISOString(),
    locale: 'en_US',
    timezone: 'America/Los_Angeles'
  },
  {
    id: 'user-4',
    firstName: 'Michael',
    lastName: 'Brown',
    email: 'michael.brown@regalproducthub.com',
    username: 'mbrown',
    title: 'Sales Manager',
    department: 'Sales',
    roleId: 'role-manager',
    roleName: 'Manager',
    isActive: false,
    lastLogin: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(), // 7 days ago
    createdDate: new Date('2024-01-20').toISOString(),
    createdBy: 'user-1',
    lastModified: new Date('2024-03-01').toISOString(),
    locale: 'en_US',
    timezone: 'America/New_York'
  },
  {
    id: 'user-5',
    firstName: 'Emily',
    lastName: 'Davis',
    email: 'emily.davis@regalproducthub.com',
    username: 'edavis',
    phone: '+1 (555) 456-7890',
    title: 'Data Analyst',
    department: 'Analytics',
    roleId: 'role-analyst',
    roleName: 'Credit Analyst',
    isActive: true,
    lastLogin: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
    createdDate: new Date('2024-02-15').toISOString(),
    createdBy: 'user-1',
    lastModified: new Date('2024-03-08').toISOString(),
    locale: 'en_US',
    timezone: 'America/Denver'
  },
  {
    id: 'user-6',
    firstName: 'David',
    lastName: 'Wilson',
    email: 'david.wilson@regalproducthub.com',
    username: 'dwilson',
    title: 'Developer',
    department: 'IT',
    roleId: 'role-developer',
    roleName: 'Developer',
    isActive: true,
    createdDate: new Date('2024-03-01').toISOString(),
    createdBy: 'user-1',
    lastModified: new Date('2024-03-01').toISOString(),
    locale: 'en_US',
    timezone: 'America/New_York'
  }
];

// Create persisted users using centralized mock data manager
const persistedUsers = createPersistedMockData('crm_users', defaultMockUsers);

// Export MOCK_USERS for backwards compatibility (Setup Home uses this for dynamic stats)
export const MOCK_USERS = persistedUsers.data;

// ============================================
// QUERY KEYS
// ============================================

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

// ============================================
// API FUNCTIONS (Mock implementations)
// ============================================

/**
 * Fetch users with filtering
 * Uses GobodoUserProvider when USE_GOBODO_API is true, otherwise uses mockDataManager
 * Roles are automatically populated via getUserRoles() for each user
 */
export const fetchUsersForUserManagement = async (filters: UserFilters): Promise<User[]> => {
  console.log('[userQueries] fetchUsersForUserManagement - USE_GOBODO_API:', USE_GOBODO_API);

  if (USE_GOBODO_API) {
    console.log('[userQueries] fetchUsersForUserManagement - fetching from GOBODO API');
    const users = await gobodoUserProvider.getUsers(filters);
    console.log('[userQueries] fetchUsersForUserManagement - Gobodo returned:', users.length, 'users');
    // Log users with department set
    const usersWithDept = users.filter(u => u.department);
    console.log('[userQueries] fetchUsersForUserManagement - Users with department:', usersWithDept.map(u => ({
      name: `${u.firstName} ${u.lastName}`,
      department: u.department,
      title: u.title
    })));
    return users;
  }

  // Mock data implementation (only when Gobodo API is disabled)
  console.log('[userQueries] fetchUsersForUserManagement - fetching from MOCK DATA (localStorage)');
  await new Promise(resolve => setTimeout(resolve, 500));

  let filteredUsers = persistedUsers.get();
  console.log('[userQueries] fetchUsersForUserManagement - raw users from storage:', filteredUsers.length, 'users');
  console.log('[userQueries] fetchUsersForUserManagement - filters:', filters);
  // Log users with department set
  const usersWithDept = filteredUsers.filter(u => u.department);
  console.log('[userQueries] fetchUsersForUserManagement - Users with department:', usersWithDept.map(u => ({
    name: `${u.firstName} ${u.lastName}`,
    department: u.department,
    title: u.title
  })));

  // Apply search filter
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filteredUsers = filteredUsers.filter(user =>
      user.firstName.toLowerCase().includes(query) ||
      user.lastName.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.username.toLowerCase().includes(query) ||
      user.department?.toLowerCase().includes(query)
    );
  }

  // Apply status filter
  if (filters.status && filters.status !== 'all') {
    filteredUsers = filteredUsers.filter(user =>
      filters.status === 'active' ? user.isActive : !user.isActive
    );
  }

  // Apply role filter (only in mock mode - role assignment excluded from Gobodo implementation)
  if (filters.roleId && !USE_GOBODO_API) {
    filteredUsers = filteredUsers.filter(user => user.roleId === filters.roleId);
  }

  // Apply department filter
  if (filters.department) {
    filteredUsers = filteredUsers.filter(user => user.department === filters.department);
  }

  return filteredUsers;
};

/**
 * Fetch a single user by ID
 * Uses GobodoUserProvider when USE_GOBODO_API is true, otherwise uses mockDataManager
 * Roles are automatically populated via getUserRoles()
 */
const fetchUser = async (userId: string): Promise<User> => {
  if (USE_GOBODO_API) {
    const user = await gobodoUserProvider.getUserById(userId);
    if (!user) throw new Error(`User with ID ${userId} not found`);
    return user;
  }

  // Mock data implementation (only when Gobodo API is disabled)
  await new Promise(resolve => setTimeout(resolve, 300));
  const user = persistedUsers.get().find(u => u.id === userId);
  if (!user) {
    throw new Error(`User with ID ${userId} not found`);
  }
  return user;
};

/**
 * Create a new user
 * Uses GobodoUserProvider when USE_GOBODO_API is true, otherwise uses mockDataManager
 * Note: roleId and roleName from payload are ignored (role assignment excluded from this phase)
 */
const createUser = async (payload: CreateUserPayload): Promise<User> => {
  if (USE_GOBODO_API) {
    try {
      const defaultPassword = process.env.REACT_APP_DEFAULT_USER_PASSWORD ?? '';
      const createPayload: CreateUserPayload = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        username: payload.username,
        phone: payload.phone,
        title: payload.title,
        department: payload.department,
        timezone: payload.timezone,
        locale: payload.locale,
        isActive: payload.isActive,
        roleIds: payload.roleIds,
        keycloakUserId: payload.keycloakUserId,
        password: payload.password ?? defaultPassword,
      };
      return await gobodoUserProvider.createUser(createPayload);
    } catch (error) {
      console.warn('[userQueries] Failed to create user in Gobodo, falling back to mock data:', error);
      // Fall through to mock data implementation
    }
  }

  // Mock data implementation
  await new Promise(resolve => setTimeout(resolve, 800));

  const newUser: User = {
    id: `user-${Date.now()}`,
    ...payload,
    isActive: payload.isActive ?? true,
    createdDate: new Date().toISOString(),
    createdBy: 'current-user',
    lastModified: new Date().toISOString(),
  };

  return persistedUsers.add(newUser);
};

/**
 * Update an existing user
 * Uses GobodoUserProvider when USE_GOBODO_API is true, otherwise uses mockDataManager
 *
 * TODO(PE-2052, owner: Michael): Wire role assignment through to Gobodo.
 * The provider methods exist and are proven by scripts/assign-gobodo-user-roles.ts:
 *   - gobodoUserProvider.assignRolesToUser(userId, roleIds[])  // line ~960
 *   - gobodoUserProvider.removeRolesFromUser(userId, roleIds[]) // line ~1110
 *   - gobodoUserProvider.getUserRoles(userId)                   // already used here
 * EditUserModal.tsx already collects roleIds[] and passes them in payload.updates.
 * The only thing missing is this function: stop stripping roleIds, diff old vs new,
 * call assignRolesToUser for additions and removeRolesFromUser for removals.
 * See LINEAR_TICKET_USER_ROLE_ASSIGNMENT.md for full ticket spec.
 */
const updateUser = async (payload: UpdateUserPayload): Promise<User> => {
  console.log('[userQueries] updateUser - USE_GOBODO_API:', USE_GOBODO_API);
  console.log('[userQueries] updateUser - payload:', payload);

  if (USE_GOBODO_API) {
    try {
      console.log('[userQueries] updateUser - attempting GOBODO API update');
      // TODO(PE-2052): Replace this strip with a real role-diff + assign/remove flow.
      // Currently roleId/roleName/roleIds are silently dropped before reaching Gobodo,
      // which is why the UI shows a successful save but the user's roles never change.
      const { roleId, roleName, roleIds, ...updatesWithoutRole } = payload.updates as typeof payload.updates & { roleIds?: string[] };
      const result = await gobodoUserProvider.updateUser(payload.userId, updatesWithoutRole);
      console.log('[userQueries] updateUser - GOBODO SUCCESS:', result);
      return result;
    } catch (error) {
      console.warn('[userQueries] updateUser - GOBODO FAILED, falling back to mock data:', error);
      // Fall through to mock data implementation
    }
  }

  // Mock data implementation
  console.log('[userQueries] updateUser - using MOCK DATA (localStorage)');
  await new Promise(resolve => setTimeout(resolve, 600));

  const finalUpdates = {
    ...payload.updates,
    lastModified: new Date().toISOString(),
  };

  console.log('[userQueries] updateUser - userId:', payload.userId);
  console.log('[userQueries] updateUser - updates:', finalUpdates);

  const updatedUser = persistedUsers.update(payload.userId, finalUpdates);
  if (!updatedUser) {
    throw new Error(`User with ID ${payload.userId} not found`);
  }

  console.log('[userQueries] updateUser - result:', updatedUser);
  console.log('[userQueries] updateUser - all users after update:', persistedUsers.get());

  return updatedUser;
};

/**
 * Toggle user active status
 * TODO: Replace with actual API call to PATCH /api/users/:id/status
 */
const toggleUserStatus = async (userId: string): Promise<User> => {
  await new Promise(resolve => setTimeout(resolve, 500));

  const currentUser = persistedUsers.get().find(u => u.id === userId);
  if (!currentUser) {
    throw new Error(`User with ID ${userId} not found`);
  }

  const updatedUser = persistedUsers.update(userId, {
    isActive: !currentUser.isActive,
    lastModified: new Date().toISOString()
  });

  if (!updatedUser) {
    throw new Error(`User with ID ${userId} not found`);
  }

  return updatedUser;
};

/**
 * Delete a user (soft delete by deactivating)
 * TODO: Replace with actual API call to DELETE /api/users/:id
 */
const deleteUser = async (userId: string): Promise<void> => {
  await new Promise(resolve => setTimeout(resolve, 500));

  // Soft delete by deactivating
  const updatedUser = persistedUsers.update(userId, {
    isActive: false,
    lastModified: new Date().toISOString()
  });

  if (!updatedUser) {
    throw new Error(`User with ID ${userId} not found`);
  }
};

/**
 * Send password reset email
 * TODO: Replace with actual API call to POST /api/users/:id/reset-password
 */
const resetPassword = async (userId: string): Promise<void> => {
  await new Promise(resolve => setTimeout(resolve, 400));
  console.log(`Password reset email sent for user ${userId}`);
};

// ============================================
// REACT QUERY HOOKS
// ============================================

/**
 * Hook to fetch users with filtering
 */
export const useUsers = (
  filters: UserFilters = {},
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: () => fetchUsersForUserManagement(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled !== false,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status ?? (error as { status?: number })?.status;
      if (status === 500 || status === 502 || status === 503) return false; // Don't retry server errors
      return failureCount < 1;
    },
  });
};

/**
 * Hook to fetch a single user by ID
 */
export const useUser = (userId: string) => {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch the current logged-in user
 * In Gobodo mode: Fetches from Gobodo API
 * In mock mode: Returns/creates a mock user based on first user in persistedUsers
 */
export const useSelf = () => {
  return useQuery({
    queryKey: [...userKeys.all, 'self'] as const,
    queryFn: async () => {
      if (USE_GOBODO_API) {
        try {
          const user = await gobodoUserProvider.getSelf();
          if (!user) {
            throw new Error('Current user not found');
          }
          return user;
        } catch (error) {
          console.warn('[userQueries] Failed to fetch current user from Gobodo:', error);
          throw error;
        }
      }

      // Mock data mode: Return the first user as the "current" user
      // This allows the Profile page to work in development without Gobodo
      const users = persistedUsers.get();
      if (users.length > 0) {
        return users[0];
      }

      // If no users exist, create a default mock user
      const defaultUser: User = {
        id: 'mock-current-user',
        firstName: 'Demo',
        lastName: 'User',
        email: 'demo@regalproducthub.com',
        username: 'demouser',
        isActive: true,
        createdDate: new Date().toISOString(),
        createdBy: 'System',
        lastModified: new Date().toISOString(),
      };

      return persistedUsers.add(defaultUser);
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create a new user
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

/**
 * Hook to update an existing user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(userKeys.detail(updatedUser.id), updatedUser);
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      // Also invalidate 'self' query in case user is updating their own profile
      queryClient.invalidateQueries({ queryKey: [...userKeys.all, 'self'] });
    },
  });
};

/**
 * Hook to toggle user active status
 */
export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleUserStatus,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(userKeys.detail(updatedUser.id), updatedUser);
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

/**
 * Hook to delete a user (soft delete)
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

/**
 * Hook to send password reset email
 */
export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassword,
  });
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get user's full name
 */
export const getUserFullName = (user: User): string => {
  return `${user.firstName} ${user.lastName}`;
};

/**
 * Get user's initials for avatar
 */
export const getUserInitials = (user: User): string => {
  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
};

/**
 * Format last login time
 */
export const formatLastLogin = (lastLogin?: string): string => {
  if (!lastLogin) return 'Never';

  const date = new Date(lastLogin);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
};

/**
 * Get list of departments
 */
export const getDepartments = (): string[] => {
  return [
    'IT',
    'Credit Operations',
    'Customer Support',
    'Sales',
    'Analytics',
    'Marketing',
    'Finance',
    'Human Resources'
  ];
};

/**
 * Get list of available roles
 * Now integrated with roleQueries.ts for consistent role data
 */
export const getRoles = (): Array<{ id: string; name: string }> => {
  return getMockRoles().map(role => ({
    id: role.id,
    name: role.name
  }));
};
